import { useEffect, useState } from 'react';
import { Tab, Tabs } from '@thermon-platforms/thermon-ui-core';
import AlarmDetailModal from '../../../../alarms/alarm-detail-modal';
import { createModal } from '@thermon-platforms/thermon-ui-core';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import useAlarmTypesStore from 'store/alarm-types';
import { getAlarmSeverity } from 'store/alarms';
import { getConfigItem } from 'util/device';
import { normalizeAlarmType } from 'util/alarm';
import api from 'util/api';
import { isNumber } from 'lodash';


function StatusTabs ({alarms = [], device, status}) {
  const [alarmTypesStore] = useAlarmTypesStore();
  const [rtdReadings, setRtdReadings] = useState([]);

  useEffect(() => {
    if (device?.deviceIndices?.length > 1) {
      device.deviceIndices.forEach(index => {
        if (index == 0) return; // Skip primary device index as it is already represented in status
        api(`/devices/${device.id}/status?deviceIndex=${index}`)
          .then(async res => {
            const data = await res.json();
            setRtdReadings(prev => {
              const next = [...prev];
              next[index] = data;
              return next;
            })
          })
          .catch(() => {
            // Handle error
          });
      });
    }
  }, [device])

  function viewAlarmDetail(alarm) {
    const alarmDetailModal = createModal(<AlarmDetailModal device={device} alarm={alarm} />);
    alarmDetailModal.open()
      .then((res) => {
        // Handle side-effects
      })
      .catch(() => {/* no-op */})
  }

  return (
    <div className="status-tabs">
      <Tabs useNav={true}>
        <Tab name={t`Active Alarms`}>
          <ul className="alarm-list">
            {alarms.length ? alarms.map((alarm, i) => {
              return (
                <li key={i} onClick={() => viewAlarmDetail(alarm)}>
                  <div className={`alarm-type ${normalizeAlarmType(alarm.alarmType)} ${getAlarmSeverity(alarm)}`}></div>
                  <div className="message">
                    <h4>
                      {alarmTypesStore?.types?.find(t => t.name == alarm.alarmType)?.displayName || alarm.alarmType}
                      {alarm.deviceIndex > 0 ? ` (${alarm.deviceIndex})` : ''}
                    </h4>
                    <ul className="alarm-meta">
                      <li className="created">{new Date(alarm.createdAt).toLocaleString()}</li>
                      {alarm.acknowledged ? <li className="acknowledged">{alarm.acknowledged}</li> : <></>}
                    </ul>
                  </div>
                </li>
              );
            }) :
            <li className="ok">
              <div className="alarm-type"></div>
              <div className="message">
                <h4><Trans>No Alarms</Trans></h4>
              </div>
            </li>}
          </ul>
        </Tab>
        <Tab name={t`All Readings`}>
          <div className="all-readings">
            <table className="table-readings">
              <tbody>
                <tr>
                  <td><strong><Trans>Temperature</Trans></strong></td>
                  <td>{status.temperature == null ? <>&ndash;</> : status.temperature} &deg;{status?.temperatureUnit}</td>
                </tr>
                <tr>
                  <td><strong><Trans>Current</Trans></strong></td>
                  <td>{status.current == null ? <>&ndash;</> : status.current} {status.currentUnit}</td>
                </tr>
                <tr>
                  <td><strong><Trans>Ground Current</Trans></strong></td>
                  <td>{status.groundCurrent == null ? <>&ndash;</> : status.groundCurrent} {status.groundCurrentUnit}</td>
                </tr>
                {isNumber(status?.voltage) ? <tr>
                  <td><strong><Trans>Voltage</Trans></strong></td>
                  <td>{status.voltage} {status.voltageUnit}</td>
                </tr> : null}
                {isNumber(status?.resistance) ? <tr>
                  <td><strong><Trans>Resistance</Trans></strong></td>
                  <td>{status.resistance} {status.resistanceUnit}</td>
                </tr> : null}
                {isNumber(status?.powerLevel) ? <tr>
                  <td><strong><Trans>Power Level</Trans></strong></td>
                  <td>{status.powerLevel} %</td>
                </tr> : null}
                {isNumber(status?.powerCumulative) ? <tr>
                  <td><strong><Trans>Power Accumulator</Trans></strong></td>
                  <td>{status.powerCumulative} {status?.powerCumulativeUnit}</td>
                </tr> : null}
                {isNumber(status?.powerAccumulation) ? <tr>
                  <td><strong><Trans>Power Accumulation</Trans></strong></td>
                  <td>{status.powerAccumulation} W</td>
                </tr> : null}
                {isNumber(status?.timeSinceReset) ? <tr>
                  <td><strong><Trans>Time Since Reset</Trans></strong></td>
                  <td>{status.timeSinceReset} <Trans>Hours</Trans></td>
                </tr> : null}
                {isNumber(status?.controlInput) ? <tr>
                  <td><strong><Trans>Control Input</Trans></strong></td>
                  <td>{status.controlInput}</td>
                </tr> : null}
                {isNumber(status?.controlCount) ? <tr>
                  <td><strong><Trans>Control Count</Trans></strong></td>
                  <td>{status.controlCount}</td>
                </tr> : null}
                {isNumber(status?.frequency) ? <tr>
                  <td><strong><Trans>Frequency</Trans></strong></td>
                  <td>{status.frequency} {status.powerUnit}</td>
                </tr> : null}
                {isNumber(status?.limiterTemperature) ? <tr>
                  <td><strong><Trans>Limiter Temperature</Trans></strong></td>
                  <td>{status.limiterTemperature} &deg;{status.temperatureUnit}</td>
                </tr> : null}
                {isNumber(status?.temperatureAmbient) ? <tr>
                  <td><strong><Trans>Ambient Temperature</Trans></strong></td>
                  <td>{status.temperatureAmbient} &deg;{status.temperatureUnit}</td>
                </tr> : null}
                {isNumber(status?.temperatureLocal) ? <tr>
                  <td><strong><Trans>Local Temperature</Trans></strong></td>
                  <td>{status.temperatureLocal} &deg;{status.temperatureUnit}</td>
                </tr> : null}
              </tbody>
            </table>
            {rtdReadings.map((rtd, index) => {
              if (!rtd) return null;
              return (
                <table className="table-readings" key={`rtd-${index}`}>
                  <thead>
                    <tr>
                      <th colSpan={2}>{t`RTD ${index}`}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong><Trans>Temperature</Trans></strong></td>
                      <td>{rtd.temperature == null ? <>&ndash;</> : rtd.temperature} &deg;{rtd?.temperatureUnit}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            )}
          </div>
        </Tab>
        <Tab name={t`Set Points`}>
          <table className="table-set-points">
            <thead>
              <tr>
                <th><Trans>Set Point</Trans></th>
                <th><Trans>Low</Trans></th>
                <th><Trans>High</Trans></th>
                <th><Trans>Trip</Trans></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><Trans>Temperature</Trans></td>
                <td>{getConfigItem(device, 'TemperatureLowAlarm')?.value || <>&ndash;</>} &deg;{status?.temperatureUnit}</td>
                <td>{getConfigItem(device, 'TemperatureHighAlarm')?.value || <>&ndash;</>} &deg;{status?.temperatureUnit}</td>
                <td>{getConfigItem(device, 'TemperatureHighTrip')?.value || <>&ndash;</>} &deg;{status?.temperatureUnit}</td>
              </tr>
              <tr>
                <td><Trans>Current</Trans></td>
                <td>{getConfigItem(device, 'CurrentLowAlarm')?.value || <>&ndash;</>} A</td>
                <td>{getConfigItem(device, 'CurrentHighAlarm')?.value || <>&ndash;</>} A</td>
                <td>{getConfigItem(device, 'CurrentHighTrip')?.value || <>&ndash;</>} A</td>
              </tr>
              <tr>
                <td><Trans>Ground Current</Trans></td>
                <td></td>
                <td>{getConfigItem(device, 'GroundCurrentHighAlarm')?.value || <>&ndash;</>} mA</td>
                <td>{getConfigItem(device, 'GroundCurrentHighTrip')?.value || <>&ndash;</>} mA</td>
              </tr>
            </tbody>
          </table>
        </Tab>
        {/* <Tab name={t`Past Alarms`}>
          <ul className="alarm-list">
            <li className="ok">
              <div className="alarm-type"></div>
              <div className="message">
                <h4><Trans>No Past Alarms</Trans></h4>
              </div>
            </li>
          </ul>
        </Tab> */}
      </Tabs>
    </div>
  );
}

export default StatusTabs;
